"use client";

import React, { useMemo, ReactNode } from "react";
import { motion, useReducedMotion, Variants } from "framer-motion";

type ScrollRevealProps = {
  children: ReactNode;
  effect?:
  | "fade"
  | "fadeInUp"
  | "fadeInDown"
  | "fadeInLeft"
  | "fadeInRight"
  | "zoomIn"
  | "zoomOut";
  reset?: boolean;
  duration?: number;
  easing?: [number, number, number, number];
  disableOnMobile?: boolean;
};

const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  effect = "fadeInUp",
  duration = 1,
  easing = [0, 0.48, 0.12, 0.98],
  disableOnMobile = true,
}) => {
  const shouldReduceMotion = useReducedMotion();
  const isMobile =
    typeof window !== "undefined" ? window.innerWidth < 576 : false;

  const transition = useMemo(
    () => ({ duration, ease: easing }),
    [duration, easing]
  );

  const variants: Record<string, Variants> = {
    //
    fade: {
      hidden: {
        opacity: shouldReduceMotion || (disableOnMobile && isMobile) ? 1 : 0,
        transition,
      },
      show: {
        opacity: 1,
        transition,
      },
    },
    fadeInUp: {
      hidden: {
        opacity: shouldReduceMotion || (disableOnMobile && isMobile) ? 1 : 0,
        y: shouldReduceMotion || (disableOnMobile && isMobile) ? 0 : 120,
        transition,
      },
      show: { opacity: 1, y: 0, transition },
    },
    fadeInDown: {
      hidden: { opacity: 0, y: -120, transition },
      show: { opacity: 1, y: 0, transition },
    },
    fadeInLeft: {
      hidden: {
        opacity: 0,
        x: shouldReduceMotion || (disableOnMobile && isMobile) ? 0 : -120,
        transition,
      },
      show: { opacity: 1, x: 0, transition },
    },
    fadeInRight: {
      hidden: {
        opacity: 0,
        x: shouldReduceMotion || (disableOnMobile && isMobile) ? 0 : 120,
        transition,
      },
      show: { opacity: 1, x: 0, transition },
    },
    zoomIn: {
      hidden: { opacity: 0, scale: 0.9, transition },
      show: { opacity: 1, scale: 1, transition },
    },
    zoomOut: {
      hidden: { opacity: 0, scale: 1.1, transition },
      show: { opacity: 1, scale: 1, transition },
    },
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      variants={variants[effect]}
      viewport={{ once: true, amount: 0.2 }} // Triggers only once when it enters 20% of the viewport
      className={disableOnMobile ? "sm:block" : ""}
    >
      {children}
    </motion.div>
  );
};

export default ScrollReveal;
